import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, message, Spin, Card, Row, Col, Typography, Switch, Avatar, Select, DatePicker, Alert, Tag, Space, Divider } from 'antd';
const { TextArea } = Input;
import { UserOutlined, FileProtectOutlined, DownloadOutlined, UploadOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { TermsAndConditionsModal } from './TermsAndConditionsModal';
import { OtpVerificationModal } from './OtpVerificationModal';
import { useTheme } from '@/components/common/ThemeProvider';
import { useCreateCustomerProfile, useCustomerProfile, useUpdateCustomerProfileWithOtp, useSendOtpForPaymentInfoChange, useDeductionStatus } from "@/hooks/admin/profile";
import { PaymentInformationSection } from './PaymentInformationSection';
import CustomerLocationManagement from './CustomerLocationManagement';
import FileUpload from "@/components/common/FileUpload";
import { useUpdateFileUpload } from "@/hooks/posts/attachment";
import dayjs from 'dayjs';
import {
    showWarningNotification,
    showSuccessNotification,
    showNotification,
    showErrorNotification
} from "@/components/common/utils/notify";
import { useUsers } from "@/hooks/admin/users";
import {useAuthUser} from "@/modules/auth/context/AuthContext";
import {FspType,EmploymentType,CustomerProfileDTO} from '@/shared/api/model';

const { Title, Text } = Typography;

export const CustomerProfileManagementView: React.FC = () => {
    const [form] = Form.useForm();
    const { dark, toggleTheme } = useTheme();
    const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);
    const [isOtpModalVisible, setIsOtpModalVisible] = useState(false);
    const authUser = useAuthUser();
    const paymentFormRef = useRef<any>(null);

    const [customerProfileQuery] = useCustomerProfile();
    const { data: customerProfile, isLoading: isProfileLoading } = customerProfileQuery;
    const { mutate: saveProfile } = useCreateCustomerProfile();
    const { mutate: updateProfileWithOtp } = useUpdateCustomerProfileWithOtp();
    const { mutate: sendOtpForPaymentInfoChange } = useSendOtpForPaymentInfoChange();
    const { mutate: updateFileUpload } = useUpdateFileUpload();
    const { data: deductionStatus } = useDeductionStatus();
    // @ts-ignore
    const { updateUserProfile } = useUsers(authUser.branch);

    const [profileImage, setProfileImage] = useState<string | undefined>(authUser.profilePath);
    const [paymentInfo, setPaymentInfo] = useState<Partial<CustomerProfileDTO>>({});
    const [validationErrors, setValidationErrors] = useState<string[]>([]);

    // Function to refetch customer profile
    const refetchCustomerProfile = () => {
        // This is a workaround since the hook doesn't provide a refetch function
        // In a real implementation, you might want to use React Query's queryClient.invalidateQueries
        window.location.reload();
    };

    useEffect(() => {
        if (customerProfile) {
            form.setFieldsValue({
                fullName: customerProfile.fullName,
                phoneNumber: customerProfile.phoneNumber,
                email: customerProfile.email,
                custNo: authUser.custNo,
                idCard: authUser.idCard,
                employmentType: customerProfile.employmentType,
                employerName: customerProfile.employerName,
                employeeId: customerProfile.employeeId,
                voteNumber: customerProfile.voteNumber,
                employerContacts: customerProfile.employerContacts,
                employerAddress: customerProfile.employerAddress,
                startDate: customerProfile.startDate ? dayjs(customerProfile.startDate) : null,
                endDate: customerProfile.endDate ? dayjs(customerProfile.endDate) : null,
                nextOfKinName: customerProfile.nextOfKinName,
                nextOfKinPhoneNumber: customerProfile.nextOfKinPhoneNumber,
                nextOfKinRelationship: customerProfile.nextOfKinRelationship,
                birthDate: customerProfile.birthDate ? dayjs(customerProfile.birthDate) : null,
                maritalStatus: customerProfile.maritalStatus,
                nida: customerProfile.nida,
                confirmationDate: customerProfile.confirmationDate ? dayjs(customerProfile.confirmationDate) : null
            });
        }
    }, [customerProfile, form, authUser]);

    // Validate payment information
    const validatePaymentInfo = () => {
        const errors: string[] = [];
        
        if (!paymentInfo.disbursmentFspType) {
            errors.push("Please select a disbursement channel");
        }
        
        if (!paymentInfo.disbursementFspCode) {
            errors.push("Please select a bank or mobile money provider");
        }
        
        if (!paymentInfo.disbursementAccRef) {
            errors.push("Please enter an account number or mobile number");
        }
        
        if (paymentInfo.disbursmentFspType === FspType.BANK && !paymentInfo.accountName) {
            errors.push("Please enter an account name");
        }
        
        setValidationErrors(errors);
        return errors.length === 0;
    };

    const onFinish = async (values: CustomerProfileDTO) => {
        // Combine form values with payment info
        const completeProfile = {
            ...values,
            ...paymentInfo
        };

        if (customerProfile) {
            try {
                sendOtpForPaymentInfoChange();
                setIsOtpModalVisible(true);
            } catch (error: any) {
                message.error(`Error sending OTP: ${error?.message || 'Unknown error'}`);
            }
        } else {
            try {
                // @ts-ignore
                saveProfile(completeProfile);
                message.success('Profile saved successfully');
                refetchCustomerProfile();
            } catch (error: any) {
                message.error(`Error saving profile: ${error?.message || 'Unknown error'}`);
            }
        }
    };

    const showTermsModal = () => {
        setIsTermsModalVisible(true);
    };

    const handleOtpVerification = (otp: string) => {
        const updatedProfile = { 
            ...form.getFieldsValue(), 
            ...paymentInfo,
            otp 
        };
        try {
            updateProfileWithOtp(updatedProfile);
            message.success('Profile updated successfully');
            setIsOtpModalVisible(false);
            refetchCustomerProfile();
        } catch (error: any) {
            message.error(`Error updating profile: ${error?.message || 'Unknown error'}`);
        }
    };

    const handlePaymentInfoChange = (paymentData: Partial<CustomerProfileDTO>) => {
        setPaymentInfo(paymentData);
        // Clear validation errors when user makes changes
        setValidationErrors([]);
    };

    const onFileUploadSuccess = (response: any) => {
        const fileMetaData = {
            ...response,
            category: 'PROFILE',
            fileName: `profile_${authUser.userId}`,
        };
        updateFileUpload(fileMetaData, {
            onSuccess: async () => {
                const profilePath = "/api/files/preview/" + response.relativePath;
                setProfileImage(profilePath);
                try {
                    const userData = {
                        userId: authUser.userId,
                        profilePath
                    };
                    // @ts-ignore
                    await updateUserProfile(userData);
                    showSuccessNotification('User Saved', 'Profile picture has been updated successfully.', dark);
                } catch (error: any) {
                    showErrorNotification( 'Error', 'Failed to update profile picture. Please try again.', dark);
                }
            },
            onError: (error: Error) => {
                showWarningNotification('Error', error.message);
            }
        });
    };

    if (isProfileLoading) {
        return <Spin size="large" />;
    }

    return (
        <div className={`p-4 md:p-6 ${dark ? 'bg-gray-900 text-gray-100' : 'bg-gray-100 text-gray-800'}`}>
            <Form
                form={form}
                layout="vertical"
                onFinish={onFinish}
            >
                <Card className={dark ? 'bg-gray-800' : ''}>
                    <Title level={2} className={dark ? 'text-gray-100' : ''}>Profile Information</Title>
                    <Row gutter={[16, 24]}>
                        <Col xs={24} sm={24} md={8} lg={8}>
                            <div className="text-center mb-4">
                                <Avatar size={120} src={profileImage} icon={<UserOutlined />} />
                                <div className="mt-2">
                                    <FileUpload
                                        label="Change Profile Picture"
                                        name="profilePicture"
                                        form={form}
                                        dark={dark}
                                        onSuccess={onFileUploadSuccess}
                                        accept={"image/*"}
                                    />
                                </div>
                            </div>
                            <div className="mb-4">
                                <Text strong className={dark ? 'text-gray-300' : ''}>Theme Preference</Text>
                                <div>
                                    <Switch
                                        checkedChildren="🌙"
                                        unCheckedChildren="☀️"
                                        checked={dark}
                                        onChange={toggleTheme}
                                    />
                                </div>
                            </div>
                        </Col>
                        <Col xs={24} sm={24} md={16} lg={16}>
                            <Form.Item name="fullName" label="Full Name" rules={[{ required: true }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="phoneNumber" label="Phone Number">
                                <Input readOnly />
                            </Form.Item>
                            <Form.Item name="email" label="Email">
                                <Input readOnly />
                            </Form.Item>
                            <Form.Item name="custNo" label="Customer Number">
                                <Input readOnly />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Card className={`mt-6 ${dark ? 'bg-gray-800' : ''}`}>
                    <Title level={3} className={dark ? 'text-gray-100' : ''}>Personal Information</Title>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="birthDate" 
                                label="Date of Birth"
                                rules={[{ required: true, message: 'Please select your date of birth' }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="maritalStatus" 
                                label="Marital Status"
                                rules={[{ required: true, message: 'Please select marital status' }]}
                            >
                                <Select placeholder="Select marital status">
                                    <Select.Option value="SINGLE">Single</Select.Option>
                                    <Select.Option value="MARRIED">Married</Select.Option>
                                    <Select.Option value="DIVORCED">Divorced</Select.Option>
                                    <Select.Option value="WIDOWED">Widowed</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="nida" 
                                label="National ID (NIDA)"
                                rules={[
                                    { required: true, message: 'Please enter your National ID' },
                                    { pattern: /^[0-9]{20}$/, message: 'NIDA must be 20 digits' }
                                ]}
                            >
                                <Input placeholder="Enter 20-digit National ID" maxLength={20} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="confirmationDate" 
                                label="Membership Confirmation Date"
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Card className={`mt-6 ${dark ? 'bg-gray-800' : ''}`}>
                    <Title level={3} className={dark ? 'text-gray-100' : ''}>Employment Information</Title>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="employmentType" 
                                label="Employment Type" 
                                rules={[{ required: true, message: 'Please select employment type' }]}
                            >
                                <Select>
                                    <Select.Option value={EmploymentType.CONTRACTUAL}>Contractual</Select.Option>
                                    <Select.Option value={EmploymentType.PERMANENT}>Permanent</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="employerName" 
                                label="Employer Name"
                                rules={[{ required: true, message: 'Please enter employer name' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="employeeId" 
                                label="Check Number"
                                rules={[{ required: true, message: 'Please enter check number' }]}
                            >
                                <Input placeholder="Enter employee check number" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="voteNumber" 
                                label="Vote Number"
                                rules={[{ required: true, message: 'Please enter vote number' }]}
                            >
                                <Input placeholder="Enter vote number" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="employerContacts" 
                                label="Employer Contacts"
                            >
                                <Input placeholder="Enter employer contact information" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="employerAddress" 
                                label="Employer Address"
                                rules={[{ required: true, message: 'Please enter employer address' }]}
                            >
                                <TextArea 
                                    rows={4} 
                                    placeholder="Enter employer address"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="startDate" 
                                label="Start Date"
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (getFieldValue('employmentType') === EmploymentType.CONTRACTUAL && !value) {
                                                return Promise.reject(new Error('Start date is required for contractual employment'));
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="endDate" 
                                label="End Date"
                                rules={[
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (getFieldValue('employmentType') === EmploymentType.CONTRACTUAL && !value) {
                                                return Promise.reject(new Error('End date is required for contractual employment'));
                                            }
                                            return Promise.resolve();
                                        },
                                    }),
                                ]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Card className={`mt-6 ${dark ? 'bg-gray-800' : ''}`}>
                    <Title level={3} className={dark ? 'text-gray-100' : ''}>Next of Kin Information</Title>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="nextOfKinName" 
                                label="Next of Kin Name"
                                rules={[{ required: true, message: 'Please enter next of kin name' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="nextOfKinPhoneNumber" 
                                label="Next of Kin Phone Number"
                                rules={[{ required: true, message: 'Please enter next of kin phone number' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={24} md={12} lg={12}>
                            <Form.Item 
                                name="nextOfKinRelationship" 
                                label="Relationship"
                                rules={[{ required: true, message: 'Please enter relationship' }]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                <Card className={`mt-6 ${dark ? 'bg-gray-800' : ''}`}>
                    <Title level={3} className={dark ? 'text-gray-100' : ''}>Payment Information</Title>
                    <PaymentInformationSection
                        form={form}
                        ref={paymentFormRef}
                        onPaymentInfoChange={handlePaymentInfoChange}
                        initialValues={customerProfile}
                    />
                </Card>

                <Card className={`mt-6 ${dark ? 'bg-gray-800' : ''}`}>
                    <Title level={3} className={dark ? 'text-gray-100' : ''}>Customer Location Management</Title>
                    {/* @ts-ignore */}
                    <CustomerLocationManagement customerProfile={customerProfile} />
                </Card>

                {deductionStatus?.deductionFormRequired && (
                    <Card className={`mt-6 ${dark ? 'bg-gray-800' : ''}`} id="deduction-information">
                        <Title level={3} className={dark ? 'text-gray-100' : ''}>
                            <FileProtectOutlined style={{ marginRight: 8 }} />
                            Deduction Information
                        </Title>

                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12}>
                                <Text strong>Status: </Text>
                                {deductionStatus.signedDeductionFormUploaded ? (
                                    <Tag icon={<CheckCircleOutlined />} color="success">Signed Form Uploaded</Tag>
                                ) : (
                                    <Tag icon={<ClockCircleOutlined />} color="warning">Pending Signed Form</Tag>
                                )}
                            </Col>
                            {deductionStatus.monthlyDeductionAmount && (
                                <Col xs={24} sm={12}>
                                    <Text strong>Monthly Deduction Amount: </Text>
                                    <Text>{Number(deductionStatus.monthlyDeductionAmount).toLocaleString()} TZS</Text>
                                </Col>
                            )}
                        </Row>

                        <Divider />

                        {!deductionStatus.signedDeductionFormUploaded && deductionStatus.officialDeductionFormRef && (
                            <Alert
                                message="Action Required"
                                description="An official deduction form has been prepared for you. Please download it, sign it, and upload the signed copy below."
                                type="warning"
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        {deductionStatus.officialDeductionFormRef && (
                            <div style={{ marginBottom: 16 }}>
                                <Text strong style={{ display: 'block', marginBottom: 8 }}>Official Deduction Form:</Text>
                                <Space>
                                    <Button
                                        icon={<DownloadOutlined />}
                                        href={`/api/files/preview/${deductionStatus.officialDeductionFormRef}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        Download Official Form
                                    </Button>
                                    {deductionStatus.officialDeductionFormUploadedAt && (
                                        <Text type="secondary">
                                            Uploaded: {new Date(deductionStatus.officialDeductionFormUploadedAt).toLocaleDateString()}
                                        </Text>
                                    )}
                                </Space>
                            </div>
                        )}

                        {!deductionStatus.signedDeductionFormUploaded && (
                            <div>
                                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                    <UploadOutlined style={{ marginRight: 4 }} />
                                    Upload Signed Deduction Form:
                                </Text>
                                <FileUpload
                                    label="Upload Signed Form"
                                    name="signedDeductionForm"
                                    form={form}
                                    dark={dark}
                                    onSuccess={(response: any) => {
                                        showSuccessNotification('Signed deduction form uploaded successfully. An officer will review it shortly.');
                                    }}
                                    accept=".pdf,.jpg,.jpeg,.png"
                                />
                            </div>
                        )}

                        {deductionStatus.signedDeductionFormUploaded && deductionStatus.signedDeductionFormUploadedAt && (
                            <Alert
                                message="Signed form received"
                                description={`Uploaded on ${new Date(deductionStatus.signedDeductionFormUploadedAt).toLocaleDateString()}. An officer will review and approve it.`}
                                type="success"
                                showIcon
                            />
                        )}
                    </Card>
                )}

                {/* Main submit button for all sections */}
                <div className="mt-6 flex justify-center px-4">
                    <Button 
                        type="primary" 
                        size="large"
                        htmlType="submit"
                        className="px-8 py-4 h-auto text-lg w-full md:w-auto"
                        block={false}
                        style={{ maxWidth: '100%' }}
                    >
                        Save All Profile Information
                    </Button>
                </div>
            </Form>

            <TermsAndConditionsModal
                visible={isTermsModalVisible}
                onClose={() => setIsTermsModalVisible(false)}
            />
            <OtpVerificationModal
                visible={isOtpModalVisible}
                onVerify={handleOtpVerification}
                onCancel={() => setIsOtpModalVisible(false)}
            />
        </div>
    );
};